import React, {useEffect, useState} from "react";
import {
    TextField,
    Checkbox,
    FormControlLabel,
    Button,
    CardContent,
    Box,
    Typography,
    Card
} from "@material-ui/core";
import {useTranslation} from "react-i18next";
import {httpEndpoint, httpEndpointCustom} from "../../../common/utils/HttpUtils";
import {Provozovna} from "../../model/Provozovna";
import {useKlasifikaceSelect} from "../../raal_components/SelectOptions";
import {Klasifikace, KlasifikaceColor} from "../../model/Klasifikace";
import {Grid} from "@mui/material";
import {Mapper} from "../../../common/utils/objectmapper/Mapper";
import _ from "lodash";
import {jsonToFormUrlEncoded} from "../../../common/utils/Util";
import {showSnack} from "../../../common/component/SnackContainer";
import {Autocomplete} from "@material-ui/lab";
import {useStyleContext} from "../../context/ThemeModeContext";
import {makeStyles} from "@material-ui/core/styles";
import {useTableCache} from "../../raal_components/grid/MTCache";

export type KlasifikaceFormProps = {
    provozovnaId: string;
    endpoint?: string;
};

const defaultKlasifikace: Klasifikace = {
    klasifikace: null,
    barva: "",
    nezobrazovat: false,
    poznamka: "",
    oblibena: false
};

// CSS styly podla Klasifikace v detaile Provozovny
const useStyles = makeStyles({
        root: {
            '& .MuiAutocomplete-inputRoot': {
                paddingLeft: '6px',
                paddingTop: '6px',
                paddingBottom: '6px',
            },
            '& input': {
                height: '19px',
                padding: '4.5px 4px !important',
            },
        },
    },
);

/**
 * Standalone komponenta pre zmenu klasifikacie provozovny
 * @param provozovnaId string - id provozovny ktorej sa nastavuje klasifikacia
 */
export default function KlasifikaciaUserComponent({ provozovnaId, endpoint }: KlasifikaceFormProps): JSX.Element {
    const {t} = useTranslation();
    const klasifikaceSelectOptions = useKlasifikaceSelect({isClearable: true}).options;
    const [provozovnaData, setProvozovnaData] = useState<Provozovna>({} as Provozovna)
    const mapper = new Mapper<Provozovna>({constructor:Provozovna as {new():Provozovna}});
    const {classes} = useStyleContext();
    const klasifikaceStyles = useStyles();
    const [form, setForm] = useState<Klasifikace>(defaultKlasifikace);
    const { clearTableCache } = useTableCache(endpoint, undefined);

    // Inicializacia dat provozovny
    useEffect(() => {
        async function fetchProvozovnu() {
            const endpoint = 'user/provozovna';
            const provozovnaResult = await httpEndpoint<Provozovna>(Provozovna, `${endpoint}/${provozovnaId}`);

            setProvozovnaData(provozovnaResult.data);
            setForm(provozovnaResult.data.klasifikace ? provozovnaResult.data.klasifikace : defaultKlasifikace)
        }
        fetchProvozovnu();
    }, []);

    const handleChange = (field: string) => (event: any) => {
        if (field === 'klasifikace') {
            setForm((prev) => (
                Object.assign(
                    new Klasifikace(),
                    prev,
                    {
                        barva: KlasifikaceColor[parseInt(event)] || null,
                        klasifikace: event,
                        ...(event !== 3 && { nezobrazovat: false })
                    }
                )
            ));
        }
        if (field === 'nezobrazovat') {
            setForm((prev) => (
                Object.assign(
                    new Klasifikace(),
                    prev,
                    { ...prev, nezobrazovat: event.target.checked }
                )
            ));
        }
        if (field === 'poznamka') {
            setForm((prev) => (
                Object.assign(
                    new Klasifikace(),
                    prev,
                    { ...prev, poznamka: event.target.value === '' ? null : event.target.value}
                )
            ));
        }
    };

    /**
     * Funkcia pre porovnanie objektov Klasifikacie s prienikom ich properties
     * @param a Klasifikace
     * @param b Klasifikace
     */
    //@ts-ignore
    const areEqualIgnoringExtraProps = (a, b) => {
        a = a || {};
        b = b || {};

        const keys = _.union(Object.keys(a), Object.keys(b)).filter(
            key =>
                (a[key] !== undefined && a[key] !== null) ||
                (b[key] !== undefined && b[key] !== null)
        );

        //@ts-ignore
        const normalizeAndStrip = obj =>
            _.pickBy(
                _.toPlainObject(obj),
                v => v !== null && v !== "" && v !== undefined && v !== ''
            );

        // For empty klasifikace in Provozovna
        if (!provozovnaData?.klasifikace) {
            return _.isEqual(
                normalizeAndStrip(a),
                normalizeAndStrip(defaultKlasifikace)
            );
        }

        return _.isEqual(_.pick(normalizeAndStrip(a), keys), _.pick(normalizeAndStrip(b), keys));
    };


    // Funkcia pre submit klasifikacie pre provozovnu prevzata z casti z metody sendForm z Form.tsx
    async function submitKlasifikace() {
        // URL adresa endpointu
        const url = `user/provozovna/${provozovnaId}`;

        // priprava dat Provozovny pre endpoint
        const dataToWrite = Object.assign(new Provozovna(), {
            ...provozovnaData,
            klasifikace: form
        });

        const json = mapper.writeValueAsJson(dataToWrite);

        let method = "PUT";
        let headers = {'Content-Type': 'application/x-www-form-urlencoded; charset=utf-8'};

        const init = _.merge({method: method, headers:headers, body: jsonToFormUrlEncoded(json)});
        const result = await httpEndpointCustom(url, init);
        setProvozovnaData(result.json);

        if (result?.response.ok) {
            showSnack({
                title: t("FormLocalization.DataSaved"),
                severity: "success"
            });
            clearTableCache()
        } else {
            showSnack({
                title: t("FormLocalization.DataSavedFailed"),
                severity: "error"
            });
        }
    }

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        await submitKlasifikace();
    };

    return (
        <Card style={{height: "100%"}} className={classes.sectionBackground}>
            <CardContent style={{height: "100%", display: "flex", flexFlow: "column nowrap"}}>
                <form onSubmit={handleSubmit}>
                    <Grid container spacing={2}>
                        <Grid item xs="auto" md="auto" lg="auto">
                            <Box marginBottom={2}>
                                <Typography variant="h6" >{t("Klasifikace.klasifikace").toUpperCase()}</Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={2} md={2} lg={1}>
                            <Autocomplete
                                fullWidth
                                options={klasifikaceSelectOptions}
                                value={form.klasifikace || null}
                                className={klasifikaceStyles.root}
                                onChange={(e, newValue) =>
                                    handleChange("klasifikace")(newValue)
                                }
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        variant="outlined"
                                        fullWidth
                                        inputProps={{
                                            ...params.inputProps,
                                            readOnly: true,
                                        }}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={2} md={2} lg={1}
                              style={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                              }}
                        >
                            { form.barva && <div style={{width: "40px", height: "40px", backgroundColor: form.barva}}></div> }
                        </Grid>
                        <Grid item xs={4} md={4} lg={2}
                              style={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                              }}>
                            <FormControlLabel
                                control={<Checkbox
                                    checked={form.nezobrazovat}
                                    onChange={handleChange("nezobrazovat")}
                                    color="primary"
                                    disabled={form.klasifikace !== '3'}
                                />}
                                label={t("Klasifikace.nezobrazovat")}
                            />
                        </Grid>
                        <Grid item xs={12} md={11} lg={11}>
                            <TextField
                                fullWidth
                                label={t("Klasifikace.poznamka")}
                                value={form.poznamka}
                                onChange={handleChange("poznamka")}
                                variant="outlined"
                                multiline
                                inputProps={{maxLength: 510}}
                            />
                        </Grid>
                        <Grid item xs={12} md={1} lg={1}
                              style={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                              }}>
                            <Button
                                type="submit" variant="contained" color="primary"
                                disabled={areEqualIgnoringExtraProps(form, provozovnaData?.klasifikace)}
                            >
                                {t("Buttons.Save")}
                            </Button>
                        </Grid>
                    </Grid>
                </form>
            </CardContent>
        </Card>
    );
}